<template>
  <div class="cert-page">
    <div class="breadcrumb">
      <span class="breadcrumb-item" @click="goProfile">个人中心</span>
      <span class="breadcrumb-separator"> &gt; </span>
      <span class="breadcrumb-item" @click="goProfileInfo">个人资料</span>
      <span class="breadcrumb-separator"> &gt; </span>
      <span class="breadcrumb-item active">造价师认证</span>
    </div>

    <div class="con-box">
      <div class="page-title">造价师认证</div>
      <p class="page-tip">提交资料申请造价师资质认证，审核通过后将获得造价师权限。</p>

      <div class="cert-row">
        <span class="cert-label">认证主体</span>
        <div class="cert-chips">
          <span class="cert-chip" :class="{ active: engineerCert.entry_type === 1 }" @click="setEntryType(1)">个人</span>
          <span class="cert-chip" :class="{ active: engineerCert.entry_type === 2 }" @click="setEntryType(2)">企业</span>
        </div>
      </div>
      <div class="cert-row">
        <span class="cert-label">国内 / 国际</span>
        <div class="cert-chips">
          <span class="cert-chip" :class="{ active: engineerCert.is_international === 0 }" @click="setInternational(0)">国内</span>
          <span class="cert-chip" :class="{ active: engineerCert.is_international === 1 }" @click="setInternational(1)">国际</span>
        </div>
      </div>
      <div class="cert-row">
        <span class="cert-label">造价师级别</span>
        <div class="cert-chips">
          <span class="cert-chip" :class="{ active: engineerCert.engineer_level === 1 }" @click="setEngineerLevel(1)">一级</span>
          <span class="cert-chip" :class="{ active: engineerCert.engineer_level === 2 }" @click="setEngineerLevel(2)">二级</span>
        </div>
      </div>

      <div class="form-item">
        <label class="form-label">专业分类</label>
        <el-input
          v-model="engineerCert.category_type"
          placeholder="请输入专业分类"
          maxlength="100"
          show-word-limit
          clearable
          style="width: 100%; max-width: 560px;"
        />
      </div>
      <div class="form-item">
        <label class="form-label">专业领域</label>
        <el-input
          v-model="engineerCert.professional_field"
          placeholder="请输入您对自己的专业领域描述"
          maxlength="200"
          show-word-limit
          clearable
          style="width: 100%; max-width: 560px;"
        />
      </div>
      <div class="form-item">
        <label class="form-label">个人描述</label>
        <el-input
          v-model="engineerCert.miaoshu"
          type="textarea"
          :rows="4"
          placeholder="请输入您对自己的个人描述"
          maxlength="500"
          show-word-limit
          style="width: 100%; max-width: 560px;"
        />
      </div>
      <div class="form-item region-form-item">
        <label class="form-label">所在地区</label>
        <p class="region-hint">请勾选到具体城市（省/自治区下列出地级市）；直辖市、全国、港澳台与海外等仍为单选项。可多选；「全选」将勾选全部城市及上述单选项。</p>
        <div class="region-toolbar">
          <el-checkbox
            v-model="regionCheckAll"
            :indeterminate="regionIndeterminate"
          >
            全选
          </el-checkbox>
          <span class="region-count">已选 {{ selectedRegions.length }} 项</span>
        </div>
        <div class="region-groups">
          <div
            v-for="cat in certRegionCategories"
            :key="cat.name"
            class="region-group"
          >
            <div class="region-group-name">{{ cat.name }}</div>
            <template v-for="r in cat.regions">
              <div
                v-if="isProvinceWithCities(r)"
                :key="cat.name + '-prov-' + r"
                class="region-province-block"
              >
                <div class="region-province-title">{{ r }}</div>
                <el-checkbox-group v-model="selectedRegions" class="region-checkbox-group">
                  <el-checkbox
                    v-for="city in citiesForProvince(r)"
                    :key="r + '-' + city"
                    :label="city"
                    border
                    class="region-checkbox"
                  >
                    {{ city }}
                  </el-checkbox>
                </el-checkbox-group>
              </div>
              <el-checkbox-group
                v-else
                :key="cat.name + '-leaf-' + r"
                v-model="selectedRegions"
                class="region-checkbox-group region-checkbox-group--inline"
              >
                <el-checkbox
                  :label="r"
                  border
                  class="region-checkbox"
                >
                  {{ r }}
                </el-checkbox>
              </el-checkbox-group>
            </template>
          </div>
        </div>
      </div>
      <div class="form-item">
        <label class="form-label">其他联系方式（选填）</label>
        <el-input v-model="engineerCert.other_contact" placeholder="选填" style="max-width: 560px;" />
      </div>
      <div class="form-item">
        <label class="form-label">身份证正面</label>
        <div class="cert-uploads">
          <div class="cert-thumb" @click="$refs.engFront.click()">
            <img v-if="preview.id_card_front" :src="preview.id_card_front" alt="正面">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="engFront" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'id_card_front')">
          </div>
        </div>
      </div>
      <div class="form-item">
        <label class="form-label">身份证反面</label>
        <div class="cert-uploads">
          <div class="cert-thumb" @click="$refs.engBack.click()">
            <img v-if="preview.id_card_back" :src="preview.id_card_back" alt="反面">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="engBack" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'id_card_back')">
          </div>
        </div>
      </div>
      <div class="form-item">
        <label class="form-label">职业资格证书</label>
        <div class="cert-uploads">
          <div class="cert-thumb cert-thumb-wide" @click="$refs.engQual.click()">
            <img v-if="preview.qualification_cert" :src="preview.qualification_cert" alt="资格证">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="engQual" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'qualification_cert')">
          </div>
        </div>
      </div>
      <div v-if="engineerCert.entry_type === 2" class="form-item">
        <label class="form-label">营业执照</label>
        <div class="cert-uploads">
          <div class="cert-thumb cert-thumb-wide" @click="$refs.engBiz.click()">
            <img v-if="preview.business_license" :src="preview.business_license" alt="营业执照">
            <span v-else class="cert-thumb-ph">点击上传</span>
            <input ref="engBiz" type="file" accept="image/jpeg,image/jpg,image/png" class="cert-file" @change="onImage($event, 'business_license')">
          </div>
        </div>
      </div>

      <el-button type="primary" class="submit-btn" :loading="submitting" @click="submit">提交造价师认证</el-button>
    </div>
  </div>
</template>

<script>
import { getProfile, uploadImage, submitCostEngineerCertification } from '@/api/user'
import { CERT_REGION_CATEGORIES, getAllCertLeafRegionLabels } from '@/utils/certRegionOptions'
import { getCitiesForProvince, provinceNeedsCityPicker } from '@/utils/provinceCities'

export default {
  name: 'CostEngineerCertification',
  data() {
    return {
      selectedRegions: [],
      engineerCert: {
        entry_type: 1,
        is_international: 0,
        engineer_level: 1,
        category_type: '',
        professional_field: '',
        miaoshu: '',
        id_card_front: '',
        id_card_back: '',
        qualification_cert: '',
        business_license: '',
        other_contact: '',
        region: ''
      },
      preview: {
        id_card_front: '',
        id_card_back: '',
        qualification_cert: '',
        business_license: ''
      },
      submitting: false
    }
  },
  computed: {
    certRegionCategories() {
      return CERT_REGION_CATEGORIES
    },
    allRegionLabels() {
      return getAllCertLeafRegionLabels()
    },
    regionIndeterminate() {
      const n = this.selectedRegions.length
      const t = this.allRegionLabels.length
      return n > 0 && n < t
    },
    regionCheckAll: {
      get() {
        const t = this.allRegionLabels.length
        return t > 0 && this.selectedRegions.length === t
      },
      set(val) {
        if (val) {
          this.selectedRegions = [...this.allRegionLabels]
        } else {
          this.selectedRegions = []
        }
      }
    }
  },
  watch: {
    selectedRegions: {
      handler(arr) {
        this.engineerCert.region = arr && arr.length ? arr.join(',') : ''
      },
      deep: true
    }
  },
  async created() {
    await this.ensureRole()
  },
  beforeDestroy() {
    this.revokeAllBlobs()
  },
  methods: {
    isProvinceWithCities(label) {
      return provinceNeedsCityPicker(label)
    },
    citiesForProvince(label) {
      return getCitiesForProvince(label) || []
    },
    goProfile() {
      this.$router.push('/profile')
    },
    goProfileInfo() {
      this.$router.push({ path: '/profile/index', query: { activeMenu: '1' }})
    },
    async ensureRole() {
      try {
        const res = await getProfile()
        if (res && res.code === 1 && res.data && Number(res.data.group_id) === 2) {
          this.$message.info('您已是造价师')
          this.$router.replace({ path: '/profile/index', query: { activeMenu: '1' }})
        }
      } catch (e) {
        console.error(e)
      }
    },
    revokeBlob(url) {
      if (url && String(url).startsWith('blob:')) {
        try { URL.revokeObjectURL(url) } catch (e) { /* ignore */ }
      }
    },
    revokeAllBlobs() {
      ['id_card_front', 'id_card_back', 'qualification_cert', 'business_license'].forEach(k => {
        this.revokeBlob(this.preview[k])
      })
    },
    setEntryType(type) {
      this.engineerCert.entry_type = type
      if (type === 1) {
        this.engineerCert.business_license = ''
        this.revokeBlob(this.preview.business_license)
        this.preview.business_license = ''
      }
    },
    setInternational(v) {
      this.engineerCert.is_international = v
    },
    setEngineerLevel(v) {
      this.engineerCert.engineer_level = v
    },
    async onImage(e, fieldName) {
      const input = e.target
      const file = input.files && input.files[0]
      input.value = ''
      if (!file) return
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('只支持 JPG、JPEG、PNG 格式的图片')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }
      try {
        const response = await uploadImage(file)
        if (response && response.code === 1) {
          const url = response.data.url || response.data.fullurl || response.data
          this.engineerCert[fieldName] = typeof url === 'string' ? url : (url && url.url) || ''
          this.revokeBlob(this.preview[fieldName])
          this.preview[fieldName] = URL.createObjectURL(file)
          this.$message.success('上传成功')
        } else {
          this.$message.error(response?.msg || '上传失败')
        }
      } catch (err) {
        console.error(err)
        this.$message.error('上传失败，请重试')
      }
    },
    async submit() {
      const c = this.engineerCert
      if (!c.id_card_front || !c.id_card_back || !c.qualification_cert) {
        this.$message.warning('请上传身份证正反面与职业资格证书')
        return
      }
      if (!String(c.category_type || '').trim()) {
        this.$message.warning('请输入专业分类')
        return
      }
      if (!String(c.professional_field || '').trim()) {
        this.$message.warning('请输入专业领域')
        return
      }
      if (c.entry_type === 2 && !c.business_license) {
        this.$message.warning('企业认证请上传营业执照')
        return
      }
      this.submitting = true
      try {
        const res = await submitCostEngineerCertification({
          id_card_front: c.id_card_front,
          id_card_back: c.id_card_back,
          qualification_cert: c.qualification_cert,
          professional_field: String(c.professional_field || '').trim(),
          miaoshu: String(c.miaoshu || '').trim(),
          is_international: Number(c.is_international),
          engineer_level: Number(c.engineer_level),
          category_type: String(c.category_type || '').trim(),
          business_license: c.entry_type === 2 ? c.business_license : '',
          other_contact: (c.other_contact || '').trim(),
          region: (c.region || '').trim()
        })
        if (res && res.code === 1) {
          this.$message.success(res.msg || '提交成功，请等待审核')
          this.$router.replace({ path: '/profile/index', query: { activeMenu: '1' }})
        } else {
          this.$message.error(res?.msg || '提交失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('提交失败，请重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.cert-page {
  width: var(--page-inner-ratio, 80%);
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
  margin: 0 auto;
  box-sizing: border-box;
}

.breadcrumb {
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;
}

.breadcrumb-item {
  cursor: pointer;
  color: #1e80ff;
}

.breadcrumb-item:hover {
  text-decoration: underline;
}

.breadcrumb-item.active {
  color: #666;
  cursor: default;
}

.breadcrumb-item.active:hover {
  text-decoration: none;
}

.breadcrumb-separator {
  margin: 0 8px;
  color: #999;
}

.con-box {
  background: #fff;
  border-radius: 12px;
  padding: 24px 28px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.page-tip {
  font-size: 14px;
  color: #909399;
  line-height: 1.6;
  margin-bottom: 24px;
}

.cert-row {
  margin-bottom: 18px;
}

.cert-label {
  display: block;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 10px;
}

.cert-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cert-chip {
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid #e4e7ed;
  font-size: 14px;
  color: #606266;
  transition: all 0.2s ease;
}

.cert-chip:hover {
  border-color: #c6d9f5;
}

.cert-chip.active {
  border-color: #1e80ff;
  color: #1e80ff;
  background: #f0f8ff;
}

.form-item {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.cert-uploads {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cert-thumb {
  width: 120px;
  height: 120px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  background: #fafafa;
  transition: border-color 0.2s ease;
}

.cert-thumb:hover {
  border-color: #1e80ff;
}

.cert-thumb-wide {
  width: 200px;
  min-height: 120px;
}

.cert-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.cert-thumb-ph {
  font-size: 12px;
  color: #909399;
  padding: 8px;
  text-align: center;
  line-height: 1.4;
}

.cert-file {
  display: none;
}

.submit-btn {
  margin-top: 8px;
}

.region-form-item .region-hint {
  font-size: 13px;
  color: #909399;
  margin: 0 0 12px;
  line-height: 1.5;
}

.region-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
}

.region-count {
  font-size: 13px;
  color: #606266;
}

.region-province-block {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #e4e7ed;
}

.region-province-block:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.region-province-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.region-checkbox-group--inline {
  margin-bottom: 8px;
}

.region-groups {
  max-height: 420px;
  overflow-y: auto;
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafafa;
}

.region-group {
  margin-bottom: 16px;
}

.region-group:last-child {
  margin-bottom: 0;
}

.region-group-name {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.region-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.region-checkbox-group ::v-deep .el-checkbox {
  margin-right: 0;
  margin-left: 0;
}

.region-checkbox-group ::v-deep .el-checkbox.is-bordered {
  padding: 6px 12px;
  border-radius: 6px;
}
</style>
